import { TransactionAction, TransactionActionTypes, TransactionStatus } from '../types'
import locales from '../../../locales.en.json'
import type { SupportedNetworkIDs, WalletModalErrorCodes } from '../../../types'
import type { ContractReceipt, ContractTransaction } from '@ethersproject/contracts'

interface ResFactoryOptions {
  type: TransactionActionTypes
  message?: string
  error?: string | Error
  receipt?: ContractReceipt
  code?: WalletModalErrorCodes
  transaction?: ContractTransaction
  chainId?: SupportedNetworkIDs
}

const getTxStatus = (type: TransactionActionTypes) => {
  switch (type) {
    case 'failure':
      return TransactionStatus.FAILED
    case 'submit':
      return TransactionStatus.SUBMITTED
    case 'complete':
      return TransactionStatus.COMPLETE
    default:
      return TransactionStatus.FAILED
  }
}

const resFactory = ({
  type,
  message,
  error,
  receipt,
  code,
  transaction,
  chainId,
}: ResFactoryOptions) => {
  return {
    type,
    transactionStatus: getTxStatus(type),
    ...(receipt && { receipt }),
    ...(message && { message }),
    ...(error && { error: typeof error === 'string' ? new Error(error) : error }),
    ...(code && { code }),
    ...(transaction && { transaction }),
    ...(chainId && { chainId }),
  } as TransactionAction
}

interface ResParams {
  transaction?: ContractTransaction
  error?: Error
  code?: WalletModalErrorCodes
  receipt?: ContractReceipt
  chainId?: SupportedNetworkIDs
}

export default (options?: ResParams) => {
  return {
    txError: {
      ...resFactory({
        type: 'failure',
        message: locales.WalletTxSomethingWentWrong,
        error: options?.error,
        code: options?.code,
      }),
    },
    txNoChainId: {
      ...resFactory({
        type: 'failure',
        message: locales.WalletTxNoChainId,
        error: `chainId is not defined when creating a transaction`,
        code: options?.code,
      }),
    },
    txNoWallet: {
      ...resFactory({
        type: 'failure',
        message: locales.WalletTxNoWallet,
        error: `wallet is not defined when creating a transaction`,
        code: options?.code,
      }),
    },
    txLoading: {
      ...resFactory({ type: 'loading' }),
    },
    txSubmitFail: {
      ...resFactory({
        type: 'failure',
        message: locales.WalletTxFailedToSubmit,
        error: `called contract but got back undefined`,
        code: options?.code,
      }),
    },
    txSubmitSuccess: {
      ...resFactory({
        type: 'submit',
        message: locales.WalletTxTransactionSubmitted,
        transaction: options?.transaction,
        chainId: options?.chainId,
      }),
    },
    txComplete: {
      ...resFactory({
        type: 'complete',
        message: locales.WalletTxTransactionComplete,
        receipt: options?.receipt,
      }),
    },
  }
}
